export const STUDENT_QUICK_SIGN_IN_ERROR_CODES = {
  missingFields: 'studentRoomLogin.missingFields',
  assignmentNotFound: 'studentRoomLogin.assignmentNotFound',
  orgDisabled: 'studentRoomLogin.orgDisabled',
  assignmentDisabled: 'studentRoomLogin.assignmentDisabled',
  assignmentInactive: 'studentRoomLogin.assignmentInactive',
  assignmentSubmitted: 'studentRoomLogin.assignmentSubmitted',
  noStudents: 'studentRoomLogin.noStudents',
  noFirstNameMatch: 'studentRoomLogin.noFirstNameMatch',
  lastNameRequired: 'studentRoomLogin.lastNameRequired',
  lastNameMismatch: 'studentRoomLogin.lastNameMismatch',
  duplicateFullName: 'studentRoomLogin.duplicateFullName',
  nonStudent: 'studentRoomLogin.nonStudent',
  inactiveAccount: 'studentRoomLogin.inactiveAccount',
  portfolioSubmitted: 'studentRoomLogin.portfolioSubmitted',
} as const;

export type StudentQuickSignInErrorCode =
  (typeof STUDENT_QUICK_SIGN_IN_ERROR_CODES)[keyof typeof STUDENT_QUICK_SIGN_IN_ERROR_CODES];

const STUDENT_QUICK_SIGN_IN_ERROR_MESSAGES: Record<
  StudentQuickSignInErrorCode,
  string
> = {
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.missingFields]:
    'Room code and first name are required.',
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.assignmentNotFound]:
    'We could not find that room code. Check the code and ask your teacher for help.',
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.orgDisabled]:
    'Room Code Log-in is not available for your school yet. Ask your teacher for help.',
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.assignmentDisabled]:
    'Your teacher has not turned on Room Code Log-in for this assignment yet.',
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.assignmentInactive]:
    'This assignment is no longer active.',
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.assignmentSubmitted]:
    'This assignment has already been submitted.',
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.noStudents]:
    'No students are assigned to this room code. Ask your teacher for help.',
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.noFirstNameMatch]:
    'We could not find that first name in this group. Check the spelling.',
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.lastNameRequired]:
    'More than one student in this group has that first name. Please enter your last name.',
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.lastNameMismatch]:
    'More than one student in this group has that first name. The last name you entered does not match. Please try again.',
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.duplicateFullName]:
    'Multiple students share that first and last name in this group. Ask your teacher to update the roster.',
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.nonStudent]:
    'Only students can use Room Code Log-in.',
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.inactiveAccount]:
    'Your account is inactive. Ask your teacher for help.',
  [STUDENT_QUICK_SIGN_IN_ERROR_CODES.portfolioSubmitted]:
    'Your work has already been submitted.',
};

const STUDENT_QUICK_SIGN_IN_ERROR_SET = new Set<string>(
  Object.values(STUDENT_QUICK_SIGN_IN_ERROR_CODES)
);

export const isStudentQuickSignInErrorCode = (
  value: string
): value is StudentQuickSignInErrorCode =>
  STUDENT_QUICK_SIGN_IN_ERROR_SET.has(value);

export const getStudentQuickSignInErrorMessage = (
  error: string | null | undefined
): string => {
  if (!error) {
    return 'Unable to sign in. Please try again.';
  }

  if (error === 'CredentialsSignin') {
    return 'Unable to sign in. Check the room code and name.';
  }

  if (typeof error === 'string' && isStudentQuickSignInErrorCode(error)) {
    return STUDENT_QUICK_SIGN_IN_ERROR_MESSAGES[error];
  }

  return error;
};

type StudentQuickSignInAssignmentEntry = {
  studentReportLoginEnabled?: boolean | null;
};

type StudentQuickSignInRoomCodeAccessInput = {
  hasOrganization: boolean;
  orgEnabled: boolean;
  assignmentEnabled: boolean;
};

type StudentQuickSignInRoomCodeAccessResult = {
  canAccess: boolean;
  message: string | null;
};

export const STUDENT_QUICK_SIGN_IN_ROOM_CODE_MESSAGES = {
  assignmentDisabled: 'Room Code Log-in is turned off for this assignment.',
  orgDisabled: 'Room Code Log-in is turned off for this organization.',
  unavailable: 'Room Code Log-in is unavailable for this assignment.',
} as const;

export type StudentQuickSignInRoomCodeGateReason =
  keyof typeof STUDENT_QUICK_SIGN_IN_ROOM_CODE_MESSAGES;

export const getStudentQuickSignInAssignmentEnabled = (
  assignments?: StudentQuickSignInAssignmentEntry[] | null
): boolean =>
  assignments?.every((assignment) => assignment.studentReportLoginEnabled) ??
  false;

export const getStudentQuickSignInRoomCodeGateReason = ({
  hasOrganization,
  orgEnabled,
  assignmentEnabled,
}: StudentQuickSignInRoomCodeAccessInput): StudentQuickSignInRoomCodeGateReason | null => {
  if (assignmentEnabled && (!hasOrganization || orgEnabled === true)) {
    return null;
  }

  if (hasOrganization && !orgEnabled) {
    return 'orgDisabled';
  }

  if (!assignmentEnabled) {
    return 'assignmentDisabled';
  }

  return 'unavailable';
};

export const getStudentQuickSignInRoomCodeAccess = ({
  hasOrganization,
  orgEnabled,
  assignmentEnabled,
}: StudentQuickSignInRoomCodeAccessInput): StudentQuickSignInRoomCodeAccessResult => {
  const reason = getStudentQuickSignInRoomCodeGateReason({
    hasOrganization,
    orgEnabled,
    assignmentEnabled,
  });

  return {
    canAccess: reason === null,
    message: reason ? STUDENT_QUICK_SIGN_IN_ROOM_CODE_MESSAGES[reason] : null,
  };
};
